import { useEffect, useState } from "react";
import type { CryptoAlertData } from "@crypto-trading/common/browser";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import { useInView } from "@/hooks/useInView";
import { listExchangesAvailableAtAlert } from "@/lib/live-candles/candle-symbols";
import { alertPositions, pickBestPosition } from "@/lib/alert-positions";

const PRICE_TIMEOUT_MS = 8000;
const NEW_FOR_MS = 24 * 60 * 60 * 1000;

export const formatAlertPrice = (price: number | null | undefined) => {
  if (!price) return "Not available";
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
};

export const formatTimeAgo = (date: Date | string, long = false) => {
  const diffMs = Date.now() - new Date(date).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);
  if (minutes < 1) return "just now";
  if (long) {
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

export const formatChange = (value: number) =>
  `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

export const changeTextClass = (value: number) =>
  value >= 0 ? "text-[#4ade80]" : "text-[#f87171]";

/**
 * Everything an alert card or row shows: the live price (only while it is on
 * screen), the move since the alert, the user's best position, the venues
 * that listed the token at alert time and the trend line the api sends.
 */
export function useAlertCardData(alert: CryptoAlertData) {
  const [ref, isInView] = useInView<HTMLDivElement>({
    rootMargin: "50px",
    threshold: 0.1,
  });
  // Live price only while the card is on screen (long lists would keep
  // dozens of stream subscriptions otherwise).
  const { price: livePrice, isSubscribed } = useTokenLivePrice(
    alert.currency,
    undefined,
    isInView
  );
  const [priceTimedOut, setPriceTimedOut] = useState(false);

  useEffect(() => {
    if (isInView && !livePrice) {
      setPriceTimedOut(false);
      const timer = setTimeout(() => setPriceTimedOut(true), PRICE_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
  }, [isInView, livePrice]);

  const changeFromAlert = (price: number | null | undefined) =>
    alert.price && price ? ((price - alert.price) / alert.price) * 100 : null;

  // Power autotrade can open several positions per alert; show the best one.
  const positions = alertPositions(alert);
  const bestPosition = pickBestPosition(positions, livePrice);
  const priceChange = changeFromAlert(livePrice);

  return {
    ref,
    isInView,
    livePrice,
    isSubscribed,
    isPriceLoading: isInView && !livePrice && !priceTimedOut,
    priceChange,
    positions,
    bestPosition,
    positionLabel: positions.length > 1 ? "Best position" : "Position",
    isClosedPosition: bestPosition?.status === "CLOSED",
    priceChangeToPosition: changeFromAlert(bestPosition?.entryPrice),
    allPriceDataUnavailable:
      !bestPosition && !alert.price && !livePrice && priceChange === null,
    tradableExchanges: listExchangesAvailableAtAlert(alert),
    // Built once on the api for every user (never fetched per card).
    sparkline: alert.sparkline ?? [],
    isNew: Date.now() - new Date(alert.createdAt).getTime() < NEW_FOR_MS,
    tags: alert.tokenMetadata?.tags ?? [],
  };
}
